/**
 * CreateReservationSheet — crear una reserva (o un grup) des d'una cel·la buida del calendari
 * (runbook 6, R3; grups al runbook 7, F1).
 *
 * Titular a dalt (amb cercador de client opcional) i una `StayLineFields` per habitació. Amb una
 * estada → `POST /reservations`; amb dues o més → `POST /reservations/group`. Tot es crea **a
 * Beds24 primer** (ho fa el backend): si Beds24 falla no es desa res i el formulari es queda obert
 * amb les dades per tornar-ho a provar. Sempre `CONFIRMED` i preu a mà. Pensat per a 385 px.
 */

import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Controller,
  useFieldArray,
  useForm,
  useWatch,
  type UseFormRegisterReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ClientSearch } from '@/components/clients/ClientSearch';
import { StayLineFields } from '@/components/reservations';
import type { StaySibling } from '@/components/reservations/StayLineFields';
import { cn } from '@/lib/utils';
import {
  buildCreateReservationGroupPayload,
  buildCreateReservationPayload,
  createReservationDefaults,
  createReservationSchema,
  isLang,
  MAX_GROUP_STAYS,
  nextDay,
  RESERVATION_LANG_LABELS,
  RESERVATION_LANGS,
  RESERVATION_PAYMENT_LABELS,
  RESERVATION_PAYMENT_METHODS,
  stayFromPrevious,
  staysTotal,
  type CreateReservationFormValues,
  type CreateReservationGroupPayload,
  type CreateReservationPayload,
  type StayRange,
} from '@/lib/schemas/create-reservation';
import { useCreateReservation, type CreatedReservation } from '@/hooks/useCreateReservation';
import { useCreateReservationGroup } from '@/hooks/useCreateReservationGroup';
import { buildGroupPathFocusedOn, CALENDAR_PATH } from '@/lib/reservation-url';
import type { Client } from '@/services/clients';
import { calendarApi, type Unit } from '@/services/api';

// ============================================
// TYPES
// ============================================

/** Cel·la tocada: la unitat (`Unit.id`), el nom que es veu a la graella, el dia i la propietat. */
export interface CreateReservationTarget {
  unitId: string;
  unitName: string;
  /** `yyyy-MM-dd` del dia tocat = entrada per defecte. */
  date: string;
  propertyId: number;
  propertyName?: string;
}

export interface CreateReservationSheetProps {
  target: CreateReservationTarget | null;
  onClose: () => void;
  /** Una sola reserva creada (`POST /reservations`). */
  onCreated: (reservation: CreatedReservation) => void;
  /**
   * Un grup creat: números de Beds24 en l'ordre del grup. Si no es passa, el full navega ell
   * mateix a la ruta del grup (`buildGroupPathFocusedOn`).
   */
  onGroupCreated?: (beds24Ids: string[]) => void;
}

export interface CreateReservationFormProps {
  target: CreateReservationTarget;
  /** Unitats de la propietat per al selector; si encara no hi són, només la tocada. */
  units: readonly Unit[];
  isPending: boolean;
  onSubmit: (values: CreateReservationFormValues) => void;
  onCancel: () => void;
  /** Valors de partida (per defecte, una estada a la cel·la tocada). */
  initialValues?: CreateReservationFormValues;
}

// ============================================
// HELPERS
// ============================================

const EUR = new Intl.NumberFormat('ca-ES', { style: 'currency', currency: 'EUR' });

/** Text del botó de crear: una estada o un grup de N. */
export function createButtonLabel(stays: number): string {
  return stays >= 2 ? `Crear grup de ${stays} a Beds24` : 'Crear a Beds24';
}

/**
 * Tria el camí segons el nombre d'estades: 1 → `POST /reservations`; 2 o més → `POST
 * /reservations/group`. Pur perquè es pugui provar sense muntar el full.
 */
export function routeCreateSubmit(
  values: CreateReservationFormValues,
  handlers: {
    createOne: (payload: CreateReservationPayload) => void;
    createGroup: (payload: CreateReservationGroupPayload) => void;
  },
): 'one' | 'group' {
  if (values.stays.length >= 2) {
    handlers.createGroup(buildCreateReservationGroupPayload(values));
    return 'group';
  }
  handlers.createOne(buildCreateReservationPayload(values));
  return 'one';
}

/** Si les unitats encara no han arribat, la tocada fa de llista perquè el selector tingui valor. */
function unitsWithTarget(units: readonly Unit[], target: CreateReservationTarget): readonly Unit[] {
  if (units.some((u) => u.id === target.unitId)) return units;
  const placeholder: Unit = {
    id: target.unitId,
    beds24UnitId: 0,
    beds24RoomId: 0,
    beds24PropertyId: target.propertyId,
    name: target.unitName,
    displayName: target.unitName,
    isActive: true,
    room: null,
  };
  return [placeholder, ...units];
}

// ============================================
// FORM
// ============================================

function Field({
  id,
  label,
  error,
  className,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-1 min-w-0', className)}>
      <Label id={`${id}-label`} htmlFor={id} className="text-xs text-gray-600 mb-0">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function TextInput({
  id,
  registration,
  invalid,
  ...rest
}: {
  id: string;
  registration: UseFormRegisterReturn;
  invalid?: boolean;
} & Omit<React.ComponentProps<typeof Input>, 'id'>) {
  return (
    <Input
      id={id}
      aria-invalid={invalid || undefined}
      className={cn('h-9', invalid && 'border-red-500')}
      {...rest}
      {...registration}
    />
  );
}

/** Les altres línies del formulari, per al xoc en viu de cada `StayLineFields` («l'habitació N del grup»). */
export function siblingsOf(
  stays: ReadonlyArray<Partial<StayRange>> | undefined,
  index: number,
): StaySibling[] {
  if (!stays) return [];
  return stays.flatMap((s, i) =>
    i === index
      ? []
      : [
          {
            unit_id: s?.unit_id ?? '',
            data_entrada: s?.data_entrada ?? '',
            data_sortida: s?.data_sortida ?? '',
            label: `l'habitació ${i + 1} del grup`,
          },
        ],
  );
}

export function CreateReservationForm({
  target,
  units,
  isPending,
  onSubmit,
  onCancel,
  initialValues,
}: CreateReservationFormProps) {
  const form = useForm<CreateReservationFormValues>({
    resolver: zodResolver(createReservationSchema),
    // Els errors surten en sortir del camp, no només en prémer «Crear» (deute de l'ui-review).
    mode: 'onTouched',
    defaultValues: initialValues ?? createReservationDefaults(target.unitId, target.date, nextDay(target.date)),
  });
  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = form;

  const { fields, append, remove } = useFieldArray({ control, name: 'stays' });
  const stays = useWatch({ control, name: 'stays' });
  const clientId = useWatch({ control, name: 'client_id' });
  const count = fields.length;
  const isGroup = count >= 2;
  const total = staysTotal(stays);
  const unitOptions = unitsWithTarget(units, target);

  const addStay = () => {
    if (count >= MAX_GROUP_STAYS) return;
    const all = getValues('stays');
    append(stayFromPrevious(all[all.length - 1], target.date), { shouldFocus: false });
  };

  const handleClient = (client: Client | null) => {
    if (!client) return;
    const opts = { shouldDirty: true, shouldValidate: true } as const;
    setValue('client_id', client.id, { shouldDirty: true });
    // D'una empresa no se'n treu el nom del titular: qui s'allotja és una persona.
    if (client.tipus_client !== 'empresa') {
      if (client.nom) setValue('guestFirstName', client.nom, opts);
      if (client.cognoms) setValue('guestLastName', client.cognoms, opts);
    }
    const phone = client.mobil || client.telefon;
    if (phone) setValue('guestPhone', phone, opts);
    if (client.email) setValue('guestEmail', client.email, opts);
    if (isLang(client.idioma_preferit)) setValue('lang', client.idioma_preferit, opts);
  };

  const stayListError = errors.stays?.root?.message ?? errors.stays?.message;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0" noValidate>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Titular */}
        <section className="space-y-3" aria-labelledby="cr-holder-title">
          <h3 id="cr-holder-title" className="text-sm font-semibold text-gray-800">
            Titular
          </h3>

          <div className="space-y-1">
            <p className="text-xs text-gray-600">Client existent (opcional)</p>
            <ClientSearch
              onSelect={handleClient}
              placeholder="Buscar client o empresa…"
              className="h-9"
            />
            {clientId && (
              <p className="flex items-center justify-between text-xs text-green-700">
                <span>Vinculada a un client. Pots canviar les dades.</span>
                <button
                  type="button"
                  className="underline text-gray-600"
                  onClick={() => setValue('client_id', '', { shouldDirty: true })}
                >
                  Desvincular
                </button>
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field id="cr-first" label="Nom" error={errors.guestFirstName?.message}>
              <TextInput
                id="cr-first"
                autoComplete="off"
                registration={register('guestFirstName')}
                invalid={!!errors.guestFirstName}
              />
            </Field>
            <Field id="cr-last" label="Cognoms" error={errors.guestLastName?.message}>
              <TextInput
                id="cr-last"
                autoComplete="off"
                registration={register('guestLastName')}
                invalid={!!errors.guestLastName}
              />
            </Field>
          </div>

          <Field id="cr-phone" label="Telèfon" error={errors.guestPhone?.message}>
            <TextInput
              id="cr-phone"
              type="tel"
              inputMode="tel"
              placeholder="+34 …"
              autoComplete="off"
              registration={register('guestPhone')}
              invalid={!!errors.guestPhone}
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field id="cr-email" label="Correu (opcional)" error={errors.guestEmail?.message}>
              <TextInput
                id="cr-email"
                type="email"
                inputMode="email"
                autoComplete="off"
                registration={register('guestEmail')}
                invalid={!!errors.guestEmail}
              />
            </Field>
            <Field id="cr-lang" label="Idioma (opcional)">
              <Controller
                control={control}
                name="lang"
                render={({ field }) => (
                  <Select value={field.value || undefined} onValueChange={field.onChange}>
                    <SelectTrigger id="cr-lang" aria-labelledby="cr-lang-label" className="h-9">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESERVATION_LANGS.map((l) => (
                        <SelectItem key={l} value={l}>
                          {RESERVATION_LANG_LABELS[l]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </div>
        </section>

        {/* Habitacions */}
        <section className="space-y-3" aria-labelledby="cr-stays-title">
          <h3 id="cr-stays-title" className="text-sm font-semibold text-gray-800">
            {isGroup ? `Habitacions (${count})` : 'Habitació'}
          </h3>
          {fields.map((f, index) => (
            <StayLineFields
              key={f.id}
              control={control}
              prefix={`stays.${index}`}
              idPrefix={`cr-${index}`}
              units={unitOptions}
              propertyId={target.propertyId}
              siblings={siblingsOf(stays, index)}
              header={
                isGroup ? (
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">Habitació {index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-red-600"
                      onClick={() => remove(index)}
                      aria-label={`Treure l'habitació ${index + 1}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Treure
                    </Button>
                  </div>
                ) : undefined
              }
            />
          ))}
          {stayListError && <p className="text-xs text-red-600">{stayListError}</p>}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={addStay}
            disabled={count >= MAX_GROUP_STAYS}
          >
            <Plus className="h-4 w-4 mr-1" />
            Afegir una altra habitació
          </Button>
        </section>

        {/* Detalls */}
        <section className="space-y-3">
          {isGroup ? (
            <p className="text-xs text-gray-500">
              En un grup, el pagament, l'hora d'arribada i les notes del client es posen després a
              cada fitxa.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Field id="cr-pay" label="Pagament (opcional)">
                  <Controller
                    control={control}
                    name="metode_pagament"
                    render={({ field }) => (
                      <Select value={field.value || undefined} onValueChange={field.onChange}>
                        <SelectTrigger id="cr-pay" aria-labelledby="cr-pay-label" className="h-9">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {RESERVATION_PAYMENT_METHODS.map((m) => (
                            <SelectItem key={m} value={m}>
                              {RESERVATION_PAYMENT_LABELS[m]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>
                <Field id="cr-time" label="Arribada (opcional)" error={errors.hora_arribada?.message}>
                  <TextInput
                    id="cr-time"
                    type="time"
                    registration={register('hora_arribada')}
                    invalid={!!errors.hora_arribada}
                  />
                </Field>
              </div>
              <Field id="cr-notes-client" label="Notes del client (opcional)">
                <Textarea id="cr-notes-client" rows={2} {...register('notes_client')} />
              </Field>
            </>
          )}
          <Field id="cr-notes-int" label="Notes internes (opcional)">
            <Textarea id="cr-notes-int" rows={2} {...register('notes_internes')} />
          </Field>
        </section>
      </div>

      <div className="border-t px-4 py-3 bg-white space-y-2">
        <p className="text-sm text-gray-700 flex justify-between" aria-live="polite">
          <span>{isGroup ? `Total del grup (${count})` : 'Total'}</span>
          <span className="font-semibold">{EUR.format(total)}</span>
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel} disabled={isPending}>
            Cancel·la
          </Button>
          <Button type="submit" className="flex-[2]" disabled={isPending} aria-busy={isPending || undefined}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creant…
              </>
            ) : (
              createButtonLabel(count)
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}

// ============================================
// SHEET
// ============================================

export function CreateReservationSheet({
  target,
  onClose,
  onCreated,
  onGroupCreated,
}: CreateReservationSheetProps) {
  const navigate = useNavigate();
  const single = useCreateReservation();
  const group = useCreateReservationGroup();
  // Mateixa clau que la graella (`useCalendarUnits`): ja és al cache i no fa cap crida nova.
  const propertyId = target?.propertyId;
  const { data: units = [] } = useQuery<Unit[]>({
    queryKey: ['calendar-units', propertyId],
    queryFn: () => calendarApi.getUnits(propertyId),
    enabled: !!propertyId,
    staleTime: 1000 * 60 * 60,
  });
  // `isPending` arriba un render tard: un doble tap ràpid podria enviar dues reserves.
  const inFlightRef = useRef(false);
  const isPending = single.isPending || group.isPending;

  const release = () => {
    inFlightRef.current = false;
  };

  const handleSubmit = (values: CreateReservationFormValues) => {
    if (!target || inFlightRef.current) return;
    inFlightRef.current = true;
    // Els errors ja els mostra cada hook (toast); el formulari es queda obert amb les dades.
    routeCreateSubmit(values, {
      createOne: (payload) =>
        single.mutate(payload, { onSuccess: (created) => onCreated(created), onSettled: release }),
      createGroup: (payload) =>
        group.mutate(payload, {
          onSuccess: ({ beds24Ids }) => {
            if (onGroupCreated) {
              onGroupCreated(beds24Ids);
              return;
            }
            onClose();
            // Grups `manual-…` (propietat sense sync) no tenen URL: es queda al calendari.
            const path = beds24Ids.length > 0 ? buildGroupPathFocusedOn(beds24Ids[0], beds24Ids) : CALENDAR_PATH;
            if (path !== CALENDAR_PATH) navigate(path, { replace: true });
          },
          onSettled: release,
        }),
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isPending) onClose();
  };

  return (
    <Sheet open={!!target} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[385px] p-0 flex flex-col gap-0"
        onInteractOutside={(e) => {
          // El desplegable del cercador de client viu fora del full: que no el tanqui.
          const el = e.target as HTMLElement | null;
          if (el?.closest('[data-radix-popper-content-wrapper]')) e.preventDefault();
        }}
      >
        <SheetHeader className="px-4 pt-4 pb-2 border-b text-left">
          <SheetTitle>Nova reserva</SheetTitle>
          <SheetDescription>
            {target?.propertyName
              ? `Es crea a Beds24 confirmada · ${target.propertyName}.`
              : 'Es crea a Beds24 confirmada.'}
          </SheetDescription>
        </SheetHeader>
        {target && (
          <CreateReservationForm
            key={`${target.unitId}-${target.date}`}
            target={target}
            units={units}
            isPending={isPending}
            onSubmit={handleSubmit}
            onCancel={onClose}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

export default CreateReservationSheet;
